import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DonateButton from "@/components/DonateButton";

function FixedDolenBadge() {
  return (
    <>
      <style>{`
        @keyframes dolenPulse {
          0%, 100% {
            box-shadow: 0 0 6px 2px rgba(212,175,55,0.15),
                        0 2px 18px 0 rgba(0,0,0,0.35);
            opacity: 0.82;
          }
          50% {
            box-shadow: 0 0 14px 5px rgba(212,175,55,0.4),
                        0 2px 24px 0 rgba(0,0,0,0.4);
            opacity: 1;
          }
        }
      `}</style>
      <a
        href="https://dolendesign.com"
        target="_blank"
        rel="noopener noreferrer"
        title="Visit DolenDesign"
        style={{
          position: "fixed",
          bottom: "16px",
          right: "16px",
          zIndex: 9999,
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          padding: "8px 14px",
          borderRadius: "50px",
          background: "#111",
          border: "1px solid rgba(212,175,55,0.35)",
          textDecoration: "none",
          animation: "dolenPulse 2.6s ease-in-out infinite",
          cursor: "pointer",
          transition: "background 0.25s, border-color 0.25s, opacity 0.25s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = "#1a1a1a";
          e.currentTarget.style.borderColor = "rgba(212,175,55,0.75)";
          e.currentTarget.style.animation = "none";
          e.currentTarget.style.opacity = "1";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "#111";
          e.currentTarget.style.borderColor = "rgba(212,175,55,0.35)";
          e.currentTarget.style.animation = "dolenPulse 2.6s ease-in-out infinite";
        }}
      >
        <span style={{
          fontSize: "10px",
          color: "rgba(212,175,55,0.6)",
          lineHeight: 1,
        }}>✦</span>
        <span style={{
          color: "#fff",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
        }}>
          Made by{" "}
          <span style={{ color: "rgba(212,175,55,0.9)" }}>DolenDesign</span>
        </span>
      </a>
    </>
  );
}

function FooterQRBadge() {
  const [qrOpen, setQrOpen] = useState(false);

  return (
    <div className="relative flex flex-col items-center gap-3">
      <style>{`
        @keyframes donGlow {
          0%, 100% {
            box-shadow: 0 0 4px 1px rgba(212,175,55,0.08),
                        0 0 10px 2px rgba(212,175,55,0.04);
            opacity: 0.75;
          }
          50% {
            box-shadow: 0 0 10px 3px rgba(212,175,55,0.28),
                        0 0 22px 6px rgba(212,175,55,0.12);
            opacity: 1;
          }
        }
      `}</style>

      <button
        onClick={() => setQrOpen(!qrOpen)}
        aria-label="Show QR code for DolenDesign"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <div style={{
          width: "80px",
          height: "80px",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1.5px solid rgba(212,175,55,0.25)",
          background: "#fff",
          animation: "donGlow 2.8s ease-in-out infinite",
          transition: "transform 0.3s, border-color 0.3s",
          padding: "5px",
        }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "scale(1.08)";
            e.currentTarget.style.borderColor = "rgba(212,175,55,0.7)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.borderColor = "rgba(212,175,55,0.25)";
          }}
        >
          <img src="/images/don-design-qr.png" alt="DolenDesign QR code" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
        <span style={{ fontSize: "9px", letterSpacing: "0.15em", color: "rgba(212,175,55,0.45)", textTransform: "uppercase" }}>
          Scan · dolendesign.com
        </span>
      </button>

      {qrOpen && (
        <div
          onClick={() => setQrOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "28px",
              maxWidth: "300px",
              width: "90vw",
              textAlign: "center",
              boxShadow: "0 8px 60px rgba(0,0,0,0.5)",
            }}
          >
            <img src="/images/don-design-qr.png" alt="Scan for dolendesign.com" style={{ width: "100%", borderRadius: "8px" }} />
            <p style={{ marginTop: "14px", fontSize: "13px", color: "#555", letterSpacing: "0.04em" }}>
              Scan to visit <strong style={{ color: "#222" }}>dolendesign.com</strong>
            </p>
            <a
              href="https://dolendesign.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", marginTop: "10px", fontSize: "12px", color: "#8B5E3C", textDecoration: "underline" }}
            >
              dolendesign.com ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const { authed, logout } = useAuth();
  const path = location.pathname;

  const NavLink = ({ to, label, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium tracking-wide transition-colors hover:text-terracotta ${
        path === to ? "text-terracotta" : "text-forest/70"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-cream/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta text-cream font-serif text-lg italic">A</span>
            <span className="font-hand text-2xl leading-none text-forest">Agnes <span className="text-terracotta">107</span></span>
          </Link>
            <nav className="hidden items-center gap-5 sm:flex sm:gap-7">
            <NavLink to="/" label="The Celebration" testid="nav-home" />
            <NavLink to="/fan-wall" label="Sign the Card" testid="nav-fanwall" />
              <NavLink to="/ask-agnes" label="Ask Agnes" testid="nav-ask-agnes" />
            {authed ? (
              <>
                <NavLink to="/admin" label="Admin" testid="nav-admin" />
                <button
                  onClick={logout}
                  data-testid="nav-logout"
                  className="text-sm text-forest/60 hover:text-terracotta"
                >
                  Logout
                </button>
              </>
            ) : (
              <NavLink to="/admin/login" label="Admin" testid="nav-admin-login" />
            )}
          </nav>
          <div className="flex items-center gap-3 sm:hidden">
            <Link to="/ask-agnes" data-testid="mobile-nav-ask-agnes" className={`text-xs font-semibold uppercase tracking-[0.12em] ${path === "/ask-agnes" ? "text-terracotta" : "text-forest/70"}`}>Ask Agnes</Link>
            <button type="button" data-testid="mobile-nav-toggle" aria-label="Open navigation" onClick={() => document.getElementById("mobile-site-nav")?.classList.toggle("hidden")} className="grid h-9 w-9 place-items-center rounded-full border border-border text-forest">
              <span className="sr-only">Menu</span><span className="space-y-1"><span className="block h-px w-4 bg-current" /><span className="block h-px w-4 bg-current" /><span className="block h-px w-4 bg-current" /></span>
            </button>
          </div>
        </div>
          <nav id="mobile-site-nav" className="hidden border-t border-border/60 px-5 py-4 sm:hidden">
            <div className="flex flex-col gap-4">
              <NavLink to="/" label="The Celebration" testid="mobile-nav-home" />
              <NavLink to="/fan-wall" label="Sign the Card" testid="mobile-nav-fanwall" />
              {authed ? <><NavLink to="/admin" label="Admin" testid="mobile-nav-admin" /><button onClick={logout} className="text-left text-sm font-medium text-forest/70">Logout</button></> : <NavLink to="/admin/login" label="Admin" testid="mobile-nav-admin-login" />}
            </div>
          </nav>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="relative overflow-hidden bg-forest text-cream">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center">
          <div className="gold-line mx-auto mb-8 h-px w-32" />
          <p className="font-serif text-2xl md:text-3xl italic leading-snug text-cream/90">
            "A century of love, resilience, and joy."
          </p>
          <p className="mt-6 font-hand text-3xl text-gold">Agnes Murphy</p>
          <p className="mt-2 text-sm tracking-widest uppercase text-cream/60">
            Born 1919 · Celebrating 107 · August 22, 2026
          </p>

          <div className="mt-8 rounded-2xl border border-gold/20 bg-white/5 px-8 py-6 text-left inline-block">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3 text-center">The Celebration</p>
            <p className="font-serif text-lg text-cream font-semibold">Shiloh Baptist Church</p>
            <p className="mt-1 text-sm text-cream/70">10727 114 St NW, Edmonton, AB</p>
            <p className="mt-1 text-sm text-cream/70">August 22, 2026</p>
            <div className="mt-4 border-t border-gold/20 pt-4 text-center">
              <a
                href="mailto:dolendesign@gmail.com"
                className="text-sm text-gold/80 hover:text-gold transition-colors"
              >
                dolendesign@gmail.com
              </a>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <DonateButton variant="outline" testid="footer-donate-button" />
          </div>
          <div className="mt-12 flex justify-center">
            <FooterQRBadge />
          </div>
        </div>
      </footer>

      <FixedDolenBadge />
    </div>
  );
}
